/**
 * The terrain layers — per-cell tiles, per-edge walls, and the whole-map preset replace.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import { parseEdgeKey } from "@lancer-vtt/space";
import { edgeTerrain, setCellTerrain, setEdgeTerrain, terrainCellAt, terrainMapProblem, type EdgeMap, type EdgeTerrain, type TerrainCell, type TerrainMap } from "../terrain";
import { OPEN_GROUND_TILE, terrainCellProblem } from "../tiles";
import { cellGridMismatch, encounterGridMode, type EncounterState } from "../types";
import { worldFor } from "../world";
import { changed, type EncounterHandler } from "./context";

export const applySetTerrain: EncounterHandler<"set_terrain"> = (state, cmd) => {
  const mismatch = cellGridMismatch(encounterGridMode(state), cmd.cell);
  if (mismatch) return err(new CommandError(`set_terrain: ${mismatch}`));
  // The reducer is the wall for tile ids (`decisions/0185` point 4): an unknown id, or a
  // flag-shaped cell from a pre-v20 client, is rejected rather than stored.
  const problem = terrainCellProblem(cmd.terrain);
  if (problem !== null) return err(new CommandError(`set_terrain: the cell ${problem}`));
  const world = worldFor(state);
  // Replace the cell wholesale (normalized in setCellTerrain). Capture the prior cell
  // so the inverse restores it symmetrically — an absent cell round-trips as open
  // ground (cleared), keeping the map sparse.
  const prior = terrainCellAt(world, state.terrain, cmd.cell);
  const prev: TerrainCell = prior === undefined ? { tile: OPEN_GROUND_TILE } : structuredClone(prior);
  const next: EncounterState = {
    ...state,
    terrain: setCellTerrain({ space: world, terrain: state.terrain, cell: cmd.cell, entry: cmd.terrain }),
  };
  return ok({
    state: next,
    inverse: { type: "set_terrain", actorId: cmd.actorId, cell: cmd.cell, terrain: prev },
    events: changed(null),
  });
};

export const applySetEdge: EncounterHandler<"set_edge"> = (state, cmd) => {
  for (const cell of [cmd.a, cmd.b]) {
    const mismatch = cellGridMismatch(encounterGridMode(state), cell);
    if (mismatch) return err(new CommandError(`set_edge: ${mismatch}`));
  }
  const world = worldFor(state);
  // An edge is only meaningful between *adjacent* cells — reject otherwise so a
  // wall is never stored on a phantom boundary. The reducer is the wall: drivers
  // can't author a non-edge.
  if (world.distance(cmd.a, cmd.b) !== 1) {
    return err(
      new CommandError(
        `set_edge: cells ${world.key(cmd.a)} and ${world.key(cmd.b)} are not adjacent ` +
          `(an edge is a shared boundary between neighbors)`,
      ),
    );
  }
  // Replace the edge's flags wholesale (canonical-keyed in setEdgeTerrain, so
  // (a,b) and (b,a) hit the same wall). Capture prior for a symmetric inverse —
  // an absent edge round-trips as `{}` (cleared), keeping the map sparse.
  const prev: EdgeTerrain = { ...(edgeTerrain({ space: world, edges: state.edges, a: cmd.a, b: cmd.b }) ?? {}) };
  const next: EncounterState = {
    ...state,
    edges: setEdgeTerrain({ space: world, edges: state.edges, a: cmd.a, b: cmd.b, flags: cmd.edge }),
  };
  return ok({
    state: next,
    inverse: { type: "set_edge", actorId: cmd.actorId, a: cmd.a, b: cmd.b, edge: prev },
    events: changed(null),
  });
};

export const applyApplyTerrainMap: EncounterHandler<"apply_terrain_map"> = (state, cmd) => {
  // mapRadius must be a sane bound — a negative disk is meaningless. The reducer
  // is the wall: drivers can't author a degenerate map.
  if (!(typeof cmd.mapRadius === "number") || cmd.mapRadius < 0 || !Number.isFinite(cmd.mapRadius)) {
    return err(
      new CommandError(
        `apply_terrain_map: mapRadius must be a finite number >= 0 (got ${cmd.mapRadius})`,
      ),
    );
  }
  const problem = terrainMapProblem(cmd.terrain);
  if (problem !== null) return err(new CommandError(`apply_terrain_map: ${problem}`));
  // Capture the prior WHOLE map (terrain + edges + radius) so undo of a preset
  // load is one symmetric inverse, not N per-cell undos. Clone the captured maps
  // so the inverse can never alias live state.
  const prevTerrain: TerrainMap = { ...state.terrain };
  const prevEdges: EdgeMap = { ...state.edges };
  const prevRadius = state.mapRadius;
  // Rebuild each layer through the same sparsifiers set_terrain/set_edge use, so a
  // loaded preset is sparse-stable (defaults dropped) and round-trips identically
  // whether it came from a save file or a live edit. Cells/edges are recovered from
  // their canonical keys (a non-adjacent edge key would be malformed input — we
  // re-key it through setEdgeTerrain, which canonicalizes again, so a hand-edited
  // preset can't smuggle in a phantom wall).
  const world = worldFor(state);
  let nextTerrain: TerrainMap = {};
  for (const [key, entry] of Object.entries(cmd.terrain)) {
    nextTerrain = setCellTerrain({ space: world, terrain: nextTerrain, cell: world.parse(key), entry });
  }
  let nextEdges: EdgeMap = {};
  for (const [key, flags] of Object.entries(cmd.edges)) {
    const [ka, kb] = parseEdgeKey(key);
    nextEdges = setEdgeTerrain({ space: world, edges: nextEdges, a: world.parse(ka), b: world.parse(kb), flags });
  }
  const next: EncounterState = {
    ...state,
    terrain: nextTerrain,
    edges: nextEdges,
    mapRadius: cmd.mapRadius,
  };
  return ok({
    state: next,
    inverse: {
      type: "apply_terrain_map",
      actorId: cmd.actorId,
      terrain: prevTerrain,
      edges: prevEdges,
      mapRadius: prevRadius,
    },
    events: changed(null),
  });
};
